import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { IColumnSearchHistoryItem } from '@cbgms/components/grid/Hooks/use-list-search-history'
import { IListSettingsColumn } from '@cbgms/api'
import { useDataGridToolbarHistoryDialogStyles } from './DataGridToolbarHistoryDialog.styles'

type IFilterRecord = Pick<IListSettingsColumn, 'Name' | 'FilterOperator' | 'FilterValue'>

interface DataGridToolbarHistoryDialogProps {
  open: boolean
  onClose: () => void
  searchHistory: IColumnSearchHistoryItem[]
  onItemClick: (input: IColumnSearchHistoryItem) => void
  columnNameTranslator?: (key: string) => string
}

export function DataGridToolbarHistoryDialog(props: Readonly<DataGridToolbarHistoryDialogProps>) {
  const { open, onClose, onItemClick, columnNameTranslator } = props
  let { searchHistory } = props
  const classes = useDataGridToolbarHistoryDialogStyles()
  const { t, i18n } = useTranslation()

  // Default column name translator function
  const defaultColumnNameTranslator = (columnKey: string) => {
    return t(`entities.${columnKey}`, columnKey)
  }

  const translateColumnName = columnNameTranslator || defaultColumnNameTranslator

  const handleItemClick = (record: IColumnSearchHistoryItem) => {
    onItemClick(record)
    onClose()
  }

  searchHistory = [...searchHistory].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).slice(0, 10)

  // Group search history by date using user's current locale
  const groupedHistory = searchHistory.reduce((groups, record, index) => {
    const recordDate = record.timestamp
      ? new Date(record.timestamp).toLocaleDateString(i18n.language)
      : new Date().toLocaleDateString(i18n.language)

    if (!groups[recordDate]) {
      groups[recordDate] = []
    }
    groups[recordDate].push({ record, originalIndex: index })

    return groups
  }, {} as Record<string, Array<{ record: IColumnSearchHistoryItem; originalIndex: number }>>)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        className: classes.dialogPaper
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleBox}>
          <Typography variant='h6' component='div'>
            Search History
          </Typography>
        </Box>
        <IconButton aria-label='close' onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={!searchHistory?.length ? classes.dialogContentEmpty : classes.dialogContent}>
        {!searchHistory?.length ? (
          <Box className={classes.emptyStateBox} data-testid='empty-search-history'>
            <SearchIcon className={classes.searchIcon} />
            <Typography variant='body1' color='text.secondary' className={classes.emptyText}>
              No search history found
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Start by applying filters to create your search history.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.contentBox}>
            {Object.entries(groupedHistory).map(([date, records], groupIndex) => (
              <Box key={date}>
                {groupIndex > 0 && <Box className={classes.divider} />}

                <Typography variant='body2' color='text.secondary' className={classes.dateHeader}>
                  {date}
                </Typography>

                <Box className={classes.recordsBox}>
                  {records.map(({ record, originalIndex }) => {
                    const filterItems = record.filters.filter(f => !!f.FilterValue)

                    return (
                      <Box
                        key={originalIndex}
                        data-testid={`history-item-${originalIndex}`}
                        onClick={() => handleItemClick(record)}
                        className={classes.recordBox}
                      >
                        {filterItems.length === 0 ? (
                          <Typography variant='body2' color='text.secondary' className={classes.noFiltersText}>
                            No filters applied
                          </Typography>
                        ) : (
                          <Chip
                            key={originalIndex}
                            data-testid={`filter-chip-${originalIndex}`}
                            label={
                              <Box component='span'>
                                {filterItems
                                  .map((filter: IFilterRecord) => `${translateColumnName(filter.Name)}: ${filter.FilterValue}`)
                                  .join(' , ')}
                              </Box>
                            }
                            variant='filled'
                            size='small'
                            className={classes.chip}
                          />
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
